import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import { CategoryInput } from '../OfferDescription/components/CategorySelector';
import { KeywordInput } from '../OfferDescription/components/KeywordSelector';
import { businessKeywordsFirst, normalizeKeywords, splitKeywords } from '../OfferDescription/components/KeywordSelector/keywordValues';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import api from 'utils/api';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import addBusinessKeywords from 'modules/Business/addBusinessKeywords';
import { getApiErrorMessage } from 'utils/apiErrors';
import { formatAddressDisplay } from 'components/address/formatAddressDisplay';
import AddOrEditAddressDialog from '../OfferAudience/components/LocationLocal/components/AddAddress';
import ImageUpload from '../OfferDetails/components/ImageUpload';
import TermsAndConditionsSuggestionsDialog, {
  normalizeTerm,
} from '../TermsAndConditionsSuggestionsDialog';
const DEFAULT_TARGETING_RADIUS = 15;
const ACTION_DIALOG_Z_INDEX = 1500;
const NESTED_ACTION_DIALOG_Z_INDEX = 1600;
const NESTED_ADDRESS_SEARCH_Z_INDEX = 1700;

const ACTIVE_ACTION_BUTTON_SX = {
  '&.Mui-disabled': {
    bgcolor: 'rgba(14,117,189,0.72)',
    color: '#fff',
  },
};

const CONTEXTUAL_DIALOG_ACTIONS = {
  LOCAL_TARGETING: {
    dialog: 'location',
    label: 'Local targeting',
    Icon: LocationOnOutlinedIcon,
  },
  TERMS_AND_CONDITIONS: {
    dialog: 'terms',
    label: 'Add T&C',
    Icon: RuleOutlinedIcon,
  },
  KEYWORDS: {
    dialog: 'keywords',
    label: 'Edit keywords',
    Icon: EditOutlinedIcon,
  },
  CATEGORIES: {
    dialog: 'categories',
    label: 'Edit categories',
    Icon: EditOutlinedIcon,
  },
  OFFER_IMAGE: {
    dialog: 'image',
    label: 'Offer image',
    Icon: AddPhotoAlternateOutlinedIcon,
  },
};

function actionButtonProps(suggestedOfferAction, action) {
  const isActive = suggestedOfferAction === action;
  return {
    variant: isActive ? 'contained' : 'text',
    'aria-current': isActive ? 'step' : undefined,
    sx: isActive ? ACTIVE_ACTION_BUTTON_SX : undefined,
  };
}

const categoryId = (category) => String(category?.categoryId ?? '');

function ImageActionDialog({ offer, open, onClose, businessId, businessCategories, currentImage, keywords, onSend, disabled }) {
  const { control, reset, setValue, watch } = useForm({
    defaultValues: { offerImage: currentImage || '' },
  });
  const [categoryAssets, setCategoryAssets] = useState([]);
  const [loadError, setLoadError] = useState('');
  const selectedImage = watch('offerImage');

  useEffect(() => {
    if (!open) return;
    reset({ offerImage: currentImage || '' });
    setLoadError('');

    api.get('/category?size=200')
      .then((response) => {
        const approvedIds = new Set((businessCategories || []).map(categoryId));
        const categories = Array.isArray(response?.content) ? response.content : [];
        setCategoryAssets(categories.filter(
          (category) => approvedIds.has(categoryId(category)) && category.categoryImage
        ));
      })
      .catch((error) => {
        setCategoryAssets([]);
        setLoadError(error?.message || error?.error || 'Category images are unavailable.');
      });
  }, [businessCategories, currentImage, open, reset]);

  const applyImage = () => {
    if (disabled || !selectedImage) return;
    onSend(`Use this image for the offer: ${selectedImage}`);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" sx={{ zIndex: ACTION_DIALOG_Z_INDEX }}>
      <DialogTitle>Offer background image</DialogTitle>
      <DialogContent dividers>
        <ImageUpload
          disabled={disabled}
          control={control}
          name="offerImage"
          keywords={keywords}
          headline1={offer?.headline1}
          headline2={offer?.headline2}
          description={offer?.description}
          businessId={businessId}
          key={businessId}
          uploadUrl={`/api/offer/${businessId}/saveImage`}
          helperText="Choose a JPG or PNG up to 2 MB. We’ll optimize it before uploading."
          cropDialogZIndex={NESTED_ACTION_DIALOG_Z_INDEX}
        />

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Or choose an approved business-category image
        </Typography>
        {loadError && <Typography color="error" variant="body2">{loadError}</Typography>}
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
          {categoryAssets.map((category) => (
            <Button
              disabled={disabled}
              key={category.categoryId}
              variant={selectedImage === category.categoryImage ? 'contained' : 'outlined'}
              onClick={() => setValue('offerImage', category.categoryImage, { shouldDirty: true })}
              sx={{ textTransform: 'none', overflowWrap: 'anywhere' }}
            >
              {category.name}
            </Button>
          ))}
          {!loadError && categoryAssets.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No approved category images are available for this business.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={applyImage} disabled={disabled || !selectedImage}>
          Use image
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function KeywordsDialog({ open, onClose, businessKeywords, currentKeywords, businessId, onBusinessKeywords, onSend, disabled }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [pendingKeyword, setPendingKeyword] = useState('');
  const { control, watch } = useForm({ defaultValues: {
    keywords: businessKeywordsFirst(currentKeywords.length ? currentKeywords : businessKeywords, businessKeywords),
  } });
  const keywords = businessKeywordsFirst([...watch('keywords'), ...splitKeywords(pendingKeyword)], businessKeywords);

  const saveBusinessKeywords = async () => {
    if (disabled || saving || !keywords.length || keywords.some((keyword) => keyword.length > 255)) return;
    setSaving(true);
    try {
      const merged = await addBusinessKeywords(businessId, businessKeywords, keywords, dispatch);
      onBusinessKeywords(merged);
      enqueueSnackbar('Added keywords to your business profile', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Couldn’t add keywords to your business profile. Please try again.'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const apply = () => {
    if (disabled || !keywords.length || keywords.some((keyword) => keyword.length > 255)) return;
    onSend(`Please replace the offer keywords with ${keywords.join(', ')}.`);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" sx={{ zIndex: ACTION_DIALOG_Z_INDEX }}>
      <DialogTitle>Edit keywords</DialogTitle>
      <DialogContent dividers>
        <KeywordInput disabled={disabled} control={control} options={businessKeywords} popupZIndex={NESTED_ACTION_DIALOG_Z_INDEX}
          onPendingInputChange={setPendingKeyword} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Business keywords appear first and are highlighted. You can remove them or add your own keywords.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={saveBusinessKeywords} disabled={disabled || saving || !businessId || !keywords.length || keywords.some((keyword) => keyword.length > 255)}>
          Add to business keywords
        </Button>
        <Button variant="contained" onClick={apply}
          disabled={disabled || saving || !keywords.length || keywords.some((keyword) => keyword.length > 255)}>
          Apply keywords
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CategoriesDialog({ onClose, businessCategories, currentCategories, onSend, disabled }) {
  const { control, watch } = useForm({ defaultValues: { category: currentCategories } });
  const categories = watch('category');
  const apply = () => {
    if (disabled || !categories.length) return;
    const ids = categories.map((category) => category.categoryId);
    onSend(`Replace all offer categories with these exact categoryIds: ${JSON.stringify(ids)}.`);
    onClose();
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" sx={{ zIndex: ACTION_DIALOG_Z_INDEX }}>
      <DialogTitle>Edit categories</DialogTitle>
      <DialogContent dividers>
        <CategoryInput disabled={disabled} control={control} businessCategories={businessCategories} popupZIndex={NESTED_ACTION_DIALOG_Z_INDEX} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Select categories for this offer from our list, or type their names in chat.
          {businessCategories.length > 0 && ' Business categories are highlighted.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={apply} disabled={disabled || !categories.length}>Apply categories</Button>
      </DialogActions>
    </Dialog>
  );
}

function LocalTargetingDialog({ open, onClose, addresses, businessId, onSend, disabled }) {
  const [selections, setSelections] = useState({});
  const [localAddresses, setLocalAddresses] = useState([]);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressError, setAddressError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (open) {
      setSelections({});
      setLocalAddresses(addresses || []);
      setAddressError('');
    }
  }, [open, businessId]);

  const refreshAddresses = async () => {
    setRefreshing(true);
    setAddressError('');
    try {
      const saved = await api.get(`/business/${businessId}/addresses`);
      const primary = (addresses || []).find((address) => Number(address.addressId) === 0);
      const byId = new Map();
      if (primary) byId.set(String(primary.addressId), primary);
      (Array.isArray(saved) ? saved : []).forEach((address) => {
        byId.set(String(address.addressId), {
          ...address,
          callButtonAvailable: Boolean(address.phoneNumber || address.phone),
        });
      });
      const refreshed = [...byId.values()];
      setLocalAddresses(refreshed);
      setSelections((previous) => {
        const updated = {};
        refreshed.forEach((address) => {
          const id = String(address.addressId);
          if (previous[id]) {
            updated[id] = {
              ...previous[id],
              addressLabel: formatAddressDisplay(address),
              callButtonAvailable: address.callButtonAvailable,
              showPhone: address.callButtonAvailable && previous[id].showPhone,
            };
          }
        });
        return updated;
      });
      return refreshed;
    } catch (error) {
      setAddressError(getApiErrorMessage(error, 'Couldn’t refresh addresses. Try again.'));
      return null;
    } finally {
      setRefreshing(false);
    }
  };

  const editAddress = async (addressId) => {
    const refreshed = await refreshAddresses();
    if (!refreshed) return;
    const address = refreshed.find((item) => String(item.addressId) === String(addressId));
    if (!address) {
      setAddressError('That address is no longer available. Refresh and try again.');
      return;
    }
    setEditingAddress(address);
    setAddressDialogOpen(true);
  };

  const closeAddressDialog = () => {
    setAddressDialogOpen(false);
    setEditingAddress(null);
  };

  const selectedEntries = useMemo(
    () => Object.values(selections).filter((selection) => selection.selected),
    [selections]
  );
  const isValid = selectedEntries.length > 0
    && selectedEntries.every(({ radius }) => Number(radius) >= 2 && Number(radius) <= 100);

  const update = (address, patch) => {
    const id = String(address.addressId);
    setSelections((previous) => ({
      ...previous,
      [id]: {
        addressLabel: formatAddressDisplay(address),
        radius: DEFAULT_TARGETING_RADIUS,
        showAddress: true,
        showPhone: false,
        ...previous[id],
        ...patch,
        callButtonAvailable: address.callButtonAvailable === true,
      },
    }));
  };

  const applyTargeting = () => {
    if (disabled || refreshing || addressError || !isValid) return;
    const locations = selectedEntries.map((selection) => {
      const callButtonSetting = selection.callButtonAvailable
        ? `, ${selection.showPhone ? 'show' : 'hide'} the Call button`
        : '';
      return `within ${selection.radius} miles of ${selection.addressLabel}, `
        + `${selection.showAddress ? 'show' : 'hide'} directions`
        + callButtonSetting;
    });
    onSend(`Change local targeting to: ${locations.join('; ')}.`);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" sx={{ zIndex: ACTION_DIALOG_Z_INDEX }}>
      <DialogTitle>Local targeting</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Select the business addresses you would like to use.
        </Typography>
        <Button disabled={disabled || refreshing} onClick={() => {
          setEditingAddress(null);
          setAddressDialogOpen(true);
        }}>
          Add address
        </Button>
        {addressError && (
          <Box role="alert">
            <Typography color="error" variant="body2">{addressError}</Typography>
            <Button onClick={refreshAddresses}>Retry</Button>
          </Box>
        )}
        <Stack spacing={1.5}>
          {localAddresses.map((address) => {
            const selection = selections[String(address.addressId)] || {};
            return (
              <Box key={address.addressId} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) auto' },
                  alignItems: 'start',
                  columnGap: 1,
                }}>
                  <FormControlLabel
                    control={(
                      <Checkbox disabled={disabled}
                        checked={Boolean(selection.selected)}
                        onChange={(event) => update(address, { selected: event.target.checked })}
                      />
                    )}
                    label={formatAddressDisplay(address)}
                    sx={{ m: 0, minWidth: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { overflowWrap: 'anywhere' } }}
                  />
                  <Button disabled={disabled || refreshing}
                    onClick={() => editAddress(address.addressId)}
                    aria-label={`Edit address: ${formatAddressDisplay(address)}`}
                    sx={{ justifySelf: 'end' }}>
                    Edit address
                  </Button>
                </Box>
                <Box sx={{ pl: 4 }}>
                  <TextField
                    disabled={disabled}
                    label="Radius (miles)"
                    type="number"
                    size="small"
                    value={selection.radius ?? DEFAULT_TARGETING_RADIUS}
                    onChange={(event) => update(address, { radius: event.target.value })}
                    inputProps={{ min: 2, max: 100 }}
                    error={Boolean(selection.selected) && (Number(selection.radius) < 2 || Number(selection.radius) > 100)}
                    helperText="2–100 miles"
                  />
                  <Stack direction="row" useFlexGap flexWrap="wrap">
                    <FormControlLabel
                      control={(
                        <Checkbox disabled={disabled || !selection.selected}
                          checked={selection.showAddress !== false}
                          onChange={(event) => update(address, { showAddress: event.target.checked })}
                        />
                      )}
                      label="Show directions"
                    />
                    {address.callButtonAvailable === true && (
                      <FormControlLabel
                        control={(
                          <Checkbox disabled={disabled || !selection.selected}
                            checked={Boolean(selection.showPhone)}
                            onChange={(event) => update(address, { showPhone: event.target.checked })}
                          />
                        )}
                        label="Show call button"
                      />
                    )}
                  </Stack>
                </Box>
              </Box>
            );
          })}
          {localAddresses.length === 0 && (
            <Typography variant="body2">No saved addresses are available for this business.</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={disabled || refreshing || Boolean(addressError) || !isValid} onClick={applyTargeting}>
          Apply targeting
        </Button>
      </DialogActions>
      <AddOrEditAddressDialog
        open={addressDialogOpen}
        onClose={closeAddressDialog}
        onAddressAdded={refreshAddresses}
        businessId={businessId}
        address={editingAddress}
        sx={{ zIndex: NESTED_ACTION_DIALOG_Z_INDEX }}
        searchPopupZIndex={NESTED_ADDRESS_SEARCH_Z_INDEX}
      />
    </Dialog>
  );
}

export default function KaiyleeOfferActions({
  businessId,
  businessContext,
  currentImage,
  offer,
  disabled,
  suggestedOfferAction,
  keywordSuggestions,
  offerWorkspaceMode,
  canSaveDraft,
  onSend,
  onOpenFunding,
  onContactTeam,
  onConfigureOffer,
}) {
  const [dialog, setDialog] = useState(null);
  const handledSuggestion = useRef(null);
  const suggestionTimer = useRef(null);
  const suggestionKey = JSON.stringify(normalizeKeywords(keywordSuggestions));
  const suggestedKeywords = useMemo(() => JSON.parse(suggestionKey), [suggestionKey]);
  const cancelSuggestedEditor = () => {
    clearTimeout(suggestionTimer.current);
    handledSuggestion.current = suggestionKey;
  };
  const openDialog = (nextDialog) => {
    cancelSuggestedEditor();
    setDialog(nextDialog);
  };
  useEffect(() => {
    if (disabled || dialog || suggestedOfferAction !== 'KEYWORDS' || !suggestedKeywords.length
      || handledSuggestion.current === suggestionKey) return undefined;
    suggestionTimer.current = setTimeout(() => {
      handledSuggestion.current = suggestionKey;
      setDialog('keywords');
    }, 3000);
    return () => clearTimeout(suggestionTimer.current);
  }, [disabled, dialog, suggestedOfferAction, suggestedKeywords, suggestionKey]);
  const business = useSelector((state) => state.business);
  const [savedBusinessKeywords, setSavedBusinessKeywords] = useState(null);
  useEffect(() => { setSavedBusinessKeywords(null); }, [businessId]);
  const businessKeywords = useMemo(
    () => normalizeKeywords(String(business?.businessId) === String(businessId) && business.keywords != null
      ? business.keywords : savedBusinessKeywords ?? businessContext?.businessKeywords),
    [savedBusinessKeywords, business, businessId, businessContext?.businessKeywords]
  );
  const currentKeywords = useMemo(
    () => normalizeKeywords(offer?.keywords), [offer?.keywords]
  );
  const businessCategories = Array.isArray(businessContext?.businessCategories)
    ? businessContext.businessCategories
    : [];
  const addresses = Array.isArray(businessContext?.addresses) ? businessContext.addresses : [];
  const visibleDialogActions = Object.entries(CONTEXTUAL_DIALOG_ACTIONS).filter(
    ([action]) => offerWorkspaceMode === 'EDITING_DRAFT' || action === suggestedOfferAction
  );

  return (
    <>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} sx={{ mb: 1.25 }}>
        {suggestedOfferAction === 'CONFIGURE_OFFER' && (
          <Button
            size="small"
            startIcon={<EditOutlinedIcon />}
            disabled={disabled || !offer?.draftOfferId}
            onClick={() => onConfigureOffer?.(offer.draftOfferId)}
            {...actionButtonProps(suggestedOfferAction, 'CONFIGURE_OFFER')}
          >
            Configure offer
          </Button>
        )}
        {visibleDialogActions.map(([action, { Icon, label, dialog: actionDialog }]) => (
          <Button
            key={action}
            size="small"
            startIcon={<Icon />}
            disabled={disabled}
            onClick={() => openDialog(actionDialog)}
            {...actionButtonProps(suggestedOfferAction, action)}
          >
            {label}
          </Button>
        ))}
        {canSaveDraft && (
          <Button size="small" startIcon={<SaveOutlinedIcon />} disabled={disabled} onClick={() => onSend('Save as draft')}>
            Save draft
          </Button>
        )}
        <Button
          onClick={() => { cancelSuggestedEditor(); onOpenFunding?.(); }}
          size="small"
          startIcon={<PaymentsOutlinedIcon />}
        >
          Funding & payment
        </Button>
        <Button onClick={() => { cancelSuggestedEditor(); onContactTeam?.(); }} size="small" startIcon={<MailOutlineIcon />}>
          Contact our team
        </Button>
      </Stack>

      {dialog === 'categories' && <CategoriesDialog
        onClose={() => setDialog(null)}
        businessCategories={businessCategories}
        currentCategories={Array.isArray(offer?.categories) ? offer.categories : businessCategories}
        onSend={onSend}
        disabled={disabled}
      />}
      {dialog === 'keywords' && <KeywordsDialog
        open
        onClose={() => setDialog(null)}
        businessKeywords={businessKeywords}
        businessId={businessId}
        onBusinessKeywords={setSavedBusinessKeywords}
        currentKeywords={suggestedOfferAction === 'KEYWORDS' && suggestedKeywords.length ? suggestedKeywords : currentKeywords}
        onSend={onSend}
        disabled={disabled}
      />}
      <ImageActionDialog
        offer={offer}
        open={dialog === 'image'}
        onClose={() => setDialog(null)}
        businessId={businessId}
        businessCategories={businessCategories}
        currentImage={currentImage}
        keywords={currentKeywords}
        onSend={onSend}
        disabled={disabled}
      />
      <LocalTargetingDialog
        open={dialog === 'location'}
        onClose={() => setDialog(null)}
        addresses={addresses}
        businessId={businessId}
        onSend={onSend}
        disabled={disabled}
      />
      <TermsAndConditionsSuggestionsDialog
        open={dialog === 'terms'}
        onClose={() => setDialog(null)}
        offer={offer}
        existingTerms={offer?.termsAndConditions}
        allowCustomTerm
        disabled={disabled}
        onApply={(terms) => (
          onSend(`Add these terms and conditions: ${terms.map(normalizeTerm).join('; ')}`)
        )}
      />
    </>
  );
}
